import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

/**
 * Cliente HTTP único de la aplicación (DEC-A05 y §6 de arquitectura.md).
 *
 * · `withCredentials: true` — las cookies HttpOnly viajan solas; el JS no lee
 *   ningún token de acceso ni de refresco.
 * · Cabecera `X-CSRF-Token` en toda solicitud que mute estado. El valor se toma
 *   del `csrfToken` que devuelve el login y, tras un recargado de página, de la
 *   cookie `pa_csrf`, que es legible por JS a propósito.
 * · Ante un 401 se intenta el refresco UNA sola vez. Si falla, se notifica el
 *   fin de sesión y la aplicación redirige a la pantalla de acceso.
 */

const CSRF_COOKIE = 'pa_csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const REFRESH_PATH = '/auth/refresh';
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/** El token CSRF se mantiene en memoria: no se persiste en localStorage. */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CSRF_COOKIE}=`));
  if (!match) return null;
  const value = match.slice(CSRF_COOKIE.length + 1);
  return value.length > 0 ? decodeURIComponent(value) : null;
}

export function getCsrfToken(): string | null {
  return csrfToken ?? readCsrfCookie();
}

// ─── Aviso de sesión terminada ──────────────────────────────────────────────

type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

function notifySessionExpired(): void {
  setCsrfToken(null);
  sessionExpiredListeners.forEach((listener) => {
    listener();
  });
}

// ─── Instancia ──────────────────────────────────────────────────────────────

export const http: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 45_000,
  headers: { Accept: 'application/json' },
});

/** Opciones propias que aceptan las llamadas de este cliente. */
export interface PaRequestConfig extends AxiosRequestConfig {
  /** Excluye la solicitud del refresco automático (el propio login, p. ej.). */
  paSkipRefresh?: boolean;
}

type RetriableConfig = InternalAxiosRequestConfig & {
  /** Marca interna: evita bucles de refresco. */
  paRetried?: boolean;
  paSkipRefresh?: boolean;
};

http.interceptors.request.use((config) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) config.headers.set(CSRF_HEADER, token);
  }
  return config;
});

/** Un único refresco en vuelo, compartido por todas las solicitudes en espera. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    const config: PaRequestConfig = { paSkipRefresh: true };
    refreshInFlight = http
      .post<{ csrfToken?: string }>(REFRESH_PATH, undefined, config)
      .then((response) => {
        if (response.data?.csrfToken) setCsrfToken(response.data.csrfToken);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

http.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError) || !error.response || !error.config) {
      return Promise.reject(error);
    }

    const config = error.config as RetriableConfig;
    const isAuthFlow =
      config.paSkipRefresh === true || (config.url ?? '').startsWith(REFRESH_PATH);

    if (error.response.status !== 401 || config.paRetried === true || isAuthFlow) {
      if (error.response.status === 401 && !isAuthFlow) notifySessionExpired();
      return Promise.reject(error);
    }

    config.paRetried = true;
    const refreshed = await refreshSession();
    if (!refreshed) {
      notifySessionExpired();
      return Promise.reject(error);
    }
    return http.request(config);
  },
);

/** Descarga de archivos (exportaciones): responde con `Blob`, no con JSON. */
export async function requestBlob(
  url: string,
  params: Record<string, string | number | undefined>,
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await http.get<Blob>(url, { params, responseType: 'blob' });
  const disposition = response.headers['content-disposition'];
  const filename =
    typeof disposition === 'string'
      ? (/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1] ?? null)
      : null;
  return { blob: response.data, filename: filename ? decodeURIComponent(filename) : null };
}
